import { KEYBOARD_ROWS, NEPALI_MAP, CHAR_TO_KEY } from "../lib/nepaliKeyboard";

/**
 * On-screen keyboard visualizer.
 * Props:
 *  - language: "english" | "nepali"
 *  - activeCode: currently pressed physical key code (for flash)
 *  - nextChar: character to be typed next (to highlight suggested key)
 */
export default function Keyboard({ language, activeCode, nextChar }) {
    // Determine which key to highlight for the next expected character
    let nextKeyCode = null;
    let nextNeedsShift = false;
    if (nextChar) {
        if (language === "nepali") {
            const m = CHAR_TO_KEY[nextChar];
            if (m) {
                nextKeyCode = m.code;
                nextNeedsShift = m.shift;
            } else if (nextChar === " ") {
                nextKeyCode = "Space";
            }
        } else {
            // English: map char -> code
            const c = nextChar;
            if (c === " ") nextKeyCode = "Space";
            else if (/[a-z]/.test(c)) nextKeyCode = "Key" + c.toUpperCase();
            else if (/[A-Z]/.test(c)) {
                nextKeyCode = "Key" + c;
                nextNeedsShift = true;
            } else if (/[0-9]/.test(c)) nextKeyCode = "Digit" + c;
            else {
                const punctMap = {
                    "-": "Minus",
                    "=": "Equal",
                    "[": "BracketLeft",
                    "]": "BracketRight",
                    "\\": "Backslash",
                    ";": "Semicolon",
                    "'": "Quote",
                    ",": "Comma",
                    ".": "Period",
                    "/": "Slash",
                    "`": "Backquote",
                };
                if (punctMap[c]) nextKeyCode = punctMap[c];
                else {
                    const shiftMap = {
                        "!": "Digit1",
                        "@": "Digit2",
                        "#": "Digit3",
                        $: "Digit4",
                        "%": "Digit5",
                        "^": "Digit6",
                        "&": "Digit7",
                        "*": "Digit8",
                        "(": "Digit9",
                        ")": "Digit0",
                        _: "Minus",
                        "+": "Equal",
                        "{": "BracketLeft",
                        "}": "BracketRight",
                        "|": "Backslash",
                        ":": "Semicolon",
                        '"': "Quote",
                        "<": "Comma",
                        ">": "Period",
                        "?": "Slash",
                        "~": "Backquote",
                    };
                    if (shiftMap[c]) {
                        nextKeyCode = shiftMap[c];
                        nextNeedsShift = true;
                    }
                }
            }
        }
    }

    return (
        <div
            className="glass rounded-2xl p-4 sm:p-6 select-none w-full overflow-x-auto"
            data-testid="typing-virtual-keyboard"
        >
            <div className="flex flex-col gap-2 min-w-[720px]">
                {KEYBOARD_ROWS.map((row, ri) => (
                    <div key={ri} className="flex gap-2 justify-center">
                        {row.map((key) => {
                            const mapping = NEPALI_MAP[key.code];
                            const wide = key.wide || 4;
                            const isActive = activeCode === key.code;
                            const isNext =
                                nextKeyCode === key.code ||
                                (nextNeedsShift &&
                                    (key.code === "ShiftLeft" || key.code === "ShiftRight"));
                            return (
                                <div
                                    key={key.code}
                                    className={`kbd-key flex flex-col items-center justify-center text-xs sm:text-sm font-mono-stat ${
                                        isActive ? "active" : ""
                                    } ${isNext ? "next" : ""}`}
                                    style={{
                                        width: `${wide * 14}px`,
                                        height: "52px",
                                        minWidth: `${wide * 14}px`,
                                    }}
                                >
                                    {language === "nepali" && mapping ? (
                                        <>
                                            <span className="font-nepali text-lg leading-none">
                                                {mapping.normal}
                                            </span>
                                            <span className="text-[10px] text-[var(--lt-muted)] mt-0.5">
                                                {key.en}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="text-base leading-none">{key.en}</span>
                                            {key.enShift && (
                                                <span className="text-[10px] text-[var(--lt-muted)] mt-0.5">
                                                    {key.enShift}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
            <p className="text-xs text-[var(--lt-muted)] text-center mt-4">
                {language === "nepali"
                    ? "Traditional Nepali (Devanagari) layout — no OS switch needed. Just type on your regular keyboard."
                    : "Standard QWERTY layout. Follow the highlighted key for the next character."}
            </p>
        </div>
    );
}
